import { useParams, Link } from "react-router-dom";
import { useResearchItem, useResearchItemTags } from "@/hooks/useResearch";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { ArrowLeft } from "lucide-react";
import ResearchExportButtons from "@/components/research/ResearchExportButtons";
import { sanitizeHtml } from "@/lib/sanitizeHtml";

export default function HouseViewDetail() {
  const { id } = useParams();
  const { data: item, isLoading } = useResearchItem(id);
  const { data: tags } = useResearchItemTags(id);

  if (isLoading) return <p className="text-sm text-muted-foreground p-6">Loading...</p>;
  if (!item || item.status !== "published") {
    return (
      <div className="container mx-auto px-4 py-6">
        <p className="text-sm text-muted-foreground">This item is not currently published.</p>
        <Button asChild variant="link" className="px-0">
          <Link to="/partner/house-view"><ArrowLeft className="h-4 w-4 mr-1" /> Back to feed</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 space-y-4 max-w-4xl">
      <Button asChild variant="link" className="px-0">
        <Link to="/partner/house-view"><ArrowLeft className="h-4 w-4 mr-1" /> Back to House View</Link>
      </Button>

      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-primary">{item.title}</h1>
          {item.summary && <p className="text-muted-foreground mt-1">{item.summary}</p>}
          <div className="flex flex-wrap gap-2 mt-2 text-xs text-muted-foreground">
            <Badge variant="secondary">Published</Badge>
            {item.published_at && <span>{format(new Date(item.published_at), "dd/MM/yyyy")}</span>}
            {item.primary_axis && <span>· {item.primary_axis}</span>}
            {item.conviction && <span>· Conviction {item.conviction}/5</span>}
            {item.time_horizon && <span>· {item.time_horizon}</span>}
            {item.portfolio_level && <span>· P{item.portfolio_level}</span>}
          </div>
          {tags && tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {tags.map((t: any) => (
                <Badge key={t.taxonomy_value_id} variant="outline">
                  {t.research_taxonomy_values?.axis}: {t.research_taxonomy_values?.value}
                </Badge>
              ))}
            </div>
          )}
        </div>
        <ResearchExportButtons item={item} />
      </div>

      {(item as any).ai_house_view_draft && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="p-6">
            <h3 className="font-semibold text-primary mb-2">House View</h3>
            <p className="text-sm whitespace-pre-wrap">{(item as any).ai_house_view_draft}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-6">
          {item.body_html ? (
            <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.body_html) }} />
          ) : (
            <p className="text-muted-foreground text-sm">No content.</p>
          )}
        </CardContent>
      </Card>

      {(item as any).ai_risks && (
        <Card>
          <CardContent className="p-6">
            <h3 className="font-semibold text-primary mb-2">Key Risks</h3>
            <p className="text-sm whitespace-pre-wrap">{(item as any).ai_risks}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
